import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import type { Experience, ExperienceWithFavorite } from '@/entities/experience';
import { useExperienceEntity } from '@/entities/experience/entity';
import type { PaginatedExperiences } from '@/shared/api/experience';
import { getSeoBffV1ExperienceList } from '@/shared/api/experience';

import { getQueryParams } from '../lib/parseExperienceListQueryFromNextUrl';

import type { Ref } from 'vue';

/** Источник экскурсий — любой ответ BFF с блоком related_experiences: статья или карточка достопримечательности. */
type ExperiencesSource = { related_experiences?: PaginatedExperiences } | null;

export const useArticleExperiences = (data: Ref<ExperiencesSource>) => {
  const isLoadingExperiences = ref(false);
  const experienceEntity = useExperienceEntity();
  const { favoritesState } = storeToRefs(experienceEntity);
  const extraResults = ref<Experience[]>([]);
  const loadedCount = ref<number | undefined>(undefined);
  const loadedNextPageUrl = ref<string | null | undefined>(undefined);
  /**
   * `undefined` — догрузку ещё не делали, смотрим `related_experiences.next` из статьи.
   * После ответа get_bff_v1_experience_list — только он: `string` есть ещё страницы, `null` — конец (не откатываемся к next статьи).
   */

  const nextPageUrl = computed(() =>
    loadedNextPageUrl.value === undefined ? data.value?.related_experiences?.next : loadedNextPageUrl.value,
  );
  const totalCount = computed(() => loadedCount.value ?? data.value?.related_experiences?.count ?? 0);

  const displayedExperiences = computed<Experience[]>(() => {
    const initial = (data.value?.related_experiences?.results ?? []) as unknown as Experience[];
    return [...initial, ...extraResults.value];
  });

  const experiencesWithFavorite = computed<ExperienceWithFavorite[]>(() =>
    displayedExperiences.value.map((exp) => ({
      exp,
      favorite: favoritesState.value.some((favoriteItem) => favoriteItem.item_id === exp.id),
    })),
  );

  const showMoreButton = computed(() => {
    return totalCount.value > experiencesWithFavorite.value.length && !!nextPageUrl.value;
  });

  const handleLoadMore = async () => {
    const nextUrl = nextPageUrl.value;
    if (!nextUrl || isLoadingExperiences.value) {
      return;
    }

    isLoadingExperiences.value = true;
    try {
      const response = await getSeoBffV1ExperienceList({
        query: getQueryParams(nextUrl),
      });
      if (!response?.data) {
        return;
      }

      const batch = response.data.results as unknown as Experience[];
      extraResults.value = [...extraResults.value, ...batch];
      loadedCount.value = response.data.count;
      loadedNextPageUrl.value = typeof response.data.next === 'string' ? response.data.next : null;
    } finally {
      isLoadingExperiences.value = false;
    }
  };

  /**
   * Добавляет экскурсию в избранное.
   */
  const handleSetFavorite = (exp: Experience) => {
    experienceEntity.setFavorite(exp);
  };

  /**
   * Удаляет экскурсию из избранного.
   */
  const handleUnsetFavorite = (id: number) => {
    const experience = displayedExperiences.value.find((item) => item.id === id);

    // Сохраняет публичный emit с id, но передаёт store полный объект для Mindbox.
    if (!experience) {
      return;
    }

    experienceEntity.unsetFavorite(experience);
  };

  /**
   * Обрабатывает клик по карточке экскурсии.
   */
  const handleCardClick = (_exp: Experience) => {};

  return {
    experiencesWithFavorite,
    isLoadingExperiences,
    showMoreButton,
    handleLoadMore,
    handleSetFavorite,
    handleUnsetFavorite,
    handleCardClick,
  };
};
